import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Search from './Search';
import Detail from './Detail';
import LibraryListing from './LibraryListing';
import { Box, CircularProgress } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import config from '../config';
import { fetchConditions } from '../Utils';
import { HealthAndSafetyIcon } from '../icons';

const encodePathSegment = (value) => encodeURIComponent(String(value || '').trim());

function Conditions() {
  const { conditionKey } = useParams();
  const [searchFocused, setSearchFocused] = useState(false);
  const [conditions, setConditions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setSearchFocused(!!conditionKey);
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoadingComplete = () => {
    setSearchFocused(false);
  };

  const fetchData = async () => {
    try {
      const conditionsList = await fetchConditions(config);
      const sorted = [...(conditionsList || [])].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '')
      );
      setConditions(sorted);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching conditions:', error);
      setLoading(false);
    }
  };

  const groupedConditions = React.useMemo(() => (
    conditions.reduce((groups, condition) => {
      const firstLetter = condition.name?.[0]?.toUpperCase() || '#';
      const groupKey = /[A-Z]/.test(firstLetter) ? firstLetter : '#';
      if (!groups[groupKey]) groups[groupKey] = [];
      groups[groupKey].push(condition);
      return groups;
    }, {})
  ), [conditions]);

  const availableLetters = React.useMemo(() => Object.keys(groupedConditions).sort(), [groupedConditions]);

  const collectionPageSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Health Conditions Database | myStack",
    "description": "Explore information on various health conditions and related supplement research.",
    "url": "https://my-stack.ai/conditions",
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": conditions.map((condition, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "url": `https://my-stack.ai/conditions/${encodePathSegment(condition.key)}`,
        "name": condition.name
      }))
    }
  };

  if (loading && !conditionKey) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: conditionKey ? 'auto' : '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        pt: conditionKey ? 3 : { xs: 2, sm: 3.5 },
        backgroundColor: 'background.default'
      }}
    >
      <Helmet>
        <title>Health Conditions & Supplement Research | myStack</title>
        <meta name="description" content="Explore information on health conditions and related supplement research. Find insights for your health goals on myStack." />
        <link rel="canonical" href="https://my-stack.ai/conditions" />
        {!loading && !conditionKey && (
          <script type="application/ld+json">
            {JSON.stringify(collectionPageSchema)}
          </script>
        )}
      </Helmet>

      {!conditionKey && (
        <LibraryListing
          itemKey={conditionKey}
          groupedItems={groupedConditions}
          availableLetters={availableLetters}
          routeBase="conditions"
          searchLabel="Search conditions"
          heroTitle="Conditions Library"
          heroDescription="Search by condition. Find supplements backed by research."
          HeroIcon={HealthAndSafetyIcon}
          idPrefix="condition"
          analyticsClickEvent="library_condition_clicked"
          analyticsLetterEvent="library_az_letter_clicked"
        />
      )}

      {conditionKey && (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Search
            itemKey={conditionKey}
            dataKey="conditions"
            detailKey="card"
            routeBase="conditions"
            label="Search conditions"
          />
        </Box>
      )}

      {conditionKey && (
        <Box sx={{ width: '100%', mt: 1 }}>
          <Detail
            dataKey="conditions"
            detailKey="card"
            onLoadingComplete={handleLoadingComplete}
          />
        </Box>
      )}
    </Box>
  );
}

export default Conditions;
